import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { CheckCircle, XCircle, HelpCircle, Loader2, MapPin, AlertTriangle, Camera, Upload } from 'lucide-react';
import { motion } from 'framer-motion';

interface CorroborationPopupProps {
  isOpen: boolean;
  onClose: () => void;
  reportId: string;
  notificationId: string;
}

// Response weights for scoring (Just Culture-safe)
const RESPONSE_WEIGHTS = {
  // Specific corroboration
  specific: {
    confirmed: 1.0,
    confirmed_with_photo: 1.5,
    denied: -0.3,
    no_info: 0
  },
  // General/ambient corroboration
  general: {
    confirmed_many: 1.0,
    confirmed_few: 0.6,
    denied: -0.2,
    no_info: 0
  }
};

export const CorroborationPopup = ({ 
  isOpen, 
  onClose, 
  reportId,
  notificationId
}: CorroborationPopupProps) => {
  const [report, setReport] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [notes, setNotes] = useState('');
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [corroborationType, setCorroborationType] = useState<'specific' | 'general'>('specific');
  const [timeWindowHours, setTimeWindowHours] = useState<number>(48);
  const [workerNames, setWorkerNames] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen && reportId) {
      loadData();
    }
  }, [isOpen, reportId]);

  const loadData = async () => {
    setIsFetching(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data: profileData } = await supabase
        .from('worker_profiles')
        .select('*')
        .eq('user_id', session.user.id)
        .single();

      if (!profileData) return;
      setProfile(profileData);

      // Check existing corroboration
      const { data: existingCorroboration } = await supabase
        .from('peer_corroborations')
        .select('*')
        .eq('report_id', reportId)
        .eq('corroborator_id', profileData.id)
        .maybeSingle();

      if (existingCorroboration) {
        setIsSubmitted(true);
      }

      // Fetch report with reported worker info for natural conversation
      const { data: reportData, error } = await supabase
        .from('peer_reports')
        .select(`
          id, location, hazard_category, description, photo_url, work_activity, site_id, created_at,
          reported_worker:worker_profiles!reported_worker_id(display_name, full_name)
        `)
        .eq('id', reportId)
        .single();

      if (error || !reportData) {
        toast({ title: "Error", description: "Laporan tidak ditemukan", variant: "destructive" });
        onClose();
        return;
      }

      setReport(reportData);

      // Fetch worker names for redaction (same company, optionally same site)
      const { data: workers } = await supabase
        .from('worker_profiles')
        .select('display_name, full_name')
        .eq('company_id', profileData.company_id);

      if (workers) {
        const names: string[] = [];
        workers.forEach((w: any) => {
          if (w.display_name) names.push(w.display_name);
          if (w.full_name) names.push(w.full_name);
        });
        // Filter out empty/short names, deduplicate
        const uniqueNames = [...new Set(names.filter(n => n && n.length >= 2))];
        setWorkerNames(uniqueNames);
      }

      // Get corroboration window info from notification data
      const { data: notification } = await supabase
        .from('notifications')
        .select('data')
        .eq('id', notificationId)
        .single();

      if (notification?.data) {
        const notifData = notification.data as Record<string, any>;
        if (notifData.corroboration_type) {
          setCorroborationType(notifData.corroboration_type as 'specific' | 'general');
        }
        if (notifData.time_window_hours) {
          setTimeWindowHours(notifData.time_window_hours as number);
        }
      }

    } catch (error) {
      console.error('Error loading corroboration data:', error);
    } finally {
      setIsFetching(false);
    }
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !profile) return;

    setIsUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `corroboration_${reportId}_${profile.id}_${Date.now()}.${fileExt}`;
      const filePath = `corroborations/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('hazard-photos')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('hazard-photos')
        .getPublicUrl(filePath);

      setPhotoUrl(publicUrl);
      toast({ title: "Foto berhasil diupload" });
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast({ title: "Gagal upload foto", variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };

  const handleCorroboration = async (
    status: 'confirmed' | 'confirmed_many' | 'confirmed_few' | 'denied' | 'no_info'
  ) => {
    if (!profile || !reportId) return;

    setIsLoading(true);
    try {
      // Calculate response weight
      let weight = 0;
      let normalizedStatus: 'confirmed' | 'denied' | 'no_info' = 'no_info';

      if (corroborationType === 'specific') {
        if (status === 'confirmed') {
          weight = photoUrl ? RESPONSE_WEIGHTS.specific.confirmed_with_photo : RESPONSE_WEIGHTS.specific.confirmed;
          normalizedStatus = 'confirmed';
        } else if (status === 'denied') {
          weight = RESPONSE_WEIGHTS.specific.denied;
          normalizedStatus = 'denied';
        } else {
          weight = RESPONSE_WEIGHTS.specific.no_info;
          normalizedStatus = 'no_info';
        }
      } else {
        // General corroboration
        if (status === 'confirmed_many') {
          weight = RESPONSE_WEIGHTS.general.confirmed_many;
          normalizedStatus = 'confirmed';
        } else if (status === 'confirmed_few') {
          weight = RESPONSE_WEIGHTS.general.confirmed_few;
          normalizedStatus = 'confirmed';
        } else if (status === 'denied') {
          weight = RESPONSE_WEIGHTS.general.denied;
          normalizedStatus = 'denied';
        } else {
          weight = RESPONSE_WEIGHTS.general.no_info;
          normalizedStatus = 'no_info';
        }
      }

      // Calculate time decay (responses closer to report time are worth more)
      const now = new Date();
      const reportCreated = new Date(report?.created_at || now);
      const hoursSinceReport = (now.getTime() - reportCreated.getTime()) / (1000 * 60 * 60);
      const timeDecay = Math.max(0.5, 1 - (hoursSinceReport / (timeWindowHours * 2)));

      const { error } = await supabase.from('peer_corroborations').insert({
        report_id: reportId,
        corroborator_id: profile.id,
        corroboration_status: normalizedStatus,
        notes: notes || null,
        response_weight: weight,
        response_type: corroborationType,
        photo_url: photoUrl,
        time_decay_applied: timeDecay
      });

      if (error) throw error;

      // Update corroboration request log
      await supabase
        .from('corroboration_requests_log')
        .update({ responded_at: new Date().toISOString() })
        .eq('worker_id', profile.id)
        .eq('report_id', reportId);

      // Update corroboration window peer count
      const notifResult = await supabase
        .from('notifications')
        .select('data')
        .eq('id', notificationId)
        .single();

      if (notifResult?.data) {
        const notifDataObj = notifResult.data as { data?: Record<string, any> };
        const windowData = notifDataObj.data;
        if (windowData?.window_id) {
          // Direct update for peers_responded
          const { data: currentWindow } = await supabase
            .from('corroboration_windows')
            .select('peers_responded')
            .eq('id', windowData.window_id)
            .single();

          if (currentWindow) {
            await supabase
              .from('corroboration_windows')
              .update({ peers_responded: (currentWindow.peers_responded || 0) + 1 })
              .eq('id', windowData.window_id);
          }
        }
      }

      // Mark notification as read
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      // Trigger recalculation of evidence score to update status
      try {
        await supabase.functions.invoke('calculate-evidence-score', {
          body: { reportId }
        });
        console.log('Evidence score recalculated after corroboration');
      } catch (recalcError) {
        console.error('Failed to recalculate evidence score:', recalcError);
      }

      setIsSubmitted(true);
      toast({ title: "Terima kasih!", description: "Konfirmasi kamu sudah tersimpan" });
      
      setTimeout(() => {
        onClose();
        setIsSubmitted(false);
        setNotes('');
        setPhotoUrl(null);
        setReport(null);
      }, 1500);
    } catch (error) {
      console.error('Corroboration error:', error);
      toast({ title: "Gagal menyimpan", description: "Silakan coba lagi", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setIsSubmitted(false);
    setNotes('');
    setPhotoUrl(null);
    setReport(null);
  };

  // Format time window for display
  const getTimeWindowText = () => {
    if (timeWindowHours <= 24) return 'hari ini';
    if (timeWindowHours <= 72) return 'beberapa hari terakhir';
    return 'minggu ini';
  };

  // Format time window for display

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-[340px] p-4 rounded-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-base font-bold">Konfirmasi Bahaya</DialogTitle>
        </DialogHeader>

        {isFetching ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : isSubmitted ? (
          <div className="text-center py-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="h-16 w-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-4"
            >
              <CheckCircle className="h-8 w-8 text-success" />
            </motion.div>
            <h3 className="text-lg font-bold mb-2">Sudah Dikonfirmasi</h3>
            <p className="text-muted-foreground text-sm">Terima kasih atas bantuan kamu!</p>
          </div>
        ) : report ? (
          <div className="space-y-3">
            {/* Natural conversational question - use description directly */}
            <div className="p-3 rounded-xl bg-muted/50 border">
              <p className="text-sm text-foreground leading-relaxed">
                {getTimeWindowText() === 'hari ini' ? 'Hari ini ' : 'Dalam beberapa hari terakhir '}
                saat bekerja di <span className="font-medium">{report.location}</span>, apakah kamu melihat{' '}
                <span className="font-semibold text-primary">{report.description}</span>
                {report.work_activity && ` saat ${report.work_activity}`}?
              </p>
            </div>

            {/* Photo from report (if available) - smaller */}
            {report.photo_url && (
              <img 
                src={report.photo_url} 
                alt="Hazard" 
                className="rounded-lg w-full max-h-24 object-cover"
              />
            )}

            {/* Photo upload for corroboration evidence - compact */}
            <div className="space-y-1">
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handlePhotoUpload}
                style={{
                  position: 'absolute',
                  width: '1px',
                  height: '1px',
                  padding: 0,
                  margin: '-1px',
                  overflow: 'hidden',
                  clip: 'rect(0, 0, 0, 0)',
                  opacity: 0,
                  pointerEvents: 'none',
                }}
              />
              {photoUrl ? (
                <div className="relative inline-block">
                  <img 
                    src={photoUrl} 
                    alt="Evidence" 
                    className="rounded-lg h-16 w-16 object-cover"
                  />
                  <button
                    className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground rounded-full h-5 w-5 flex items-center justify-center text-xs"
                    onClick={() => setPhotoUrl(null)}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  className="w-full h-12 rounded-xl border-dashed text-xs"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <div className="flex items-center gap-2">
                      <Camera className="h-4 w-4" />
                      <span>Tambah Foto (+50% bobot)</span>
                    </div>
                  )}
                </Button>
              )}
            </div>

            {/* Notes - compact */}
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Catatan (opsional)..."
              rows={2}
              className="rounded-xl resize-none text-sm"
            />

            {/* Response buttons - compact */}
            <div className="space-y-2 pt-1">
              {corroborationType === 'specific' ? (
                <>
                  <Button
                    onClick={() => handleCorroboration('confirmed')}
                    disabled={isLoading}
                    className="w-full h-10 rounded-xl bg-success hover:bg-success/90 text-sm"
                  >
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="mr-2 h-4 w-4" />}
                    Ya, Pernah Lihat
                  </Button>
                  
                  <Button
                    onClick={() => handleCorroboration('denied')}
                    disabled={isLoading}
                    variant="outline"
                    className="w-full h-10 rounded-xl text-sm"
                  >
                    <XCircle className="mr-2 h-4 w-4" />
                    Tidak Lihat
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={() => handleCorroboration('confirmed_many')}
                    disabled={isLoading}
                    className="w-full h-10 rounded-xl bg-success hover:bg-success/90 text-sm"
                  >
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="mr-2 h-4 w-4" />}
                    Ya, Sering Terjadi
                  </Button>

                  <Button
                    onClick={() => handleCorroboration('confirmed_few')}
                    disabled={isLoading}
                    variant="secondary"
                    className="w-full h-10 rounded-xl text-sm"
                  >
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Pernah 1-2 Kali
                  </Button>
                  
                  <Button
                    onClick={() => handleCorroboration('denied')}
                    disabled={isLoading}
                    variant="outline"
                    className="w-full h-10 rounded-xl text-sm"
                  >
                    <XCircle className="mr-2 h-4 w-4" />
                    Tidak Pernah
                  </Button>
                </>
              )}
              
              <Button
                onClick={() => handleCorroboration('no_info')}
                disabled={isLoading}
                variant="ghost"
                className="w-full h-9 rounded-xl text-xs"
              >
                <HelpCircle className="mr-1 h-3 w-3" />
                Tidak Yakin
              </Button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
